<template>
    <div>
        <div class="vgtc-searchbar align-self-stretch d-flex" ref="container">
            <div class="d-none d-sm-block" v-if="showPrefix">
                <div class="vgtc-searchbar__input-prefix d-flex justify-content-center">
                    <span class="align-self-center">T</span>
                </div>
            </div>
            <vue-typeahead-bootstrap
                class="flex-grow-1"
                :showAllResults="true"
                :disableSort="true"
                v-model="searchText"
                :data="suggestions"
                :placeholder="$t(searchPlaceholder)"
                :serializer="(s) => s.text"
                @hit="searchOnTag($event)"
                ref="typeahead"
                :inputClass="inputClass"
                :minMatchingChars="1"
            >
                <template #suggestion="{ data }">
                    <div class="d-flex align-items-center vgtc-searchbar__input__suggestion">
                        <span v-if="data.category" class="vgtc-searchbar__input__suggestion__icon">
                            <font-awesome-icon :icon="getIcon(data.category)" />
                        </span>
                        <span
                            class="vgtc-searchbar__input__suggestion__icon vgtc-searchbar__input__suggestion__icon--no-category"
                            v-else
                            >T</span
                        >
                        <span class="flex-grow-1 vgtc-searchbar__input__suggestion__text">{{ data.text }}</span>
                    </div>
                </template>
            </vue-typeahead-bootstrap>
            <div class="vgtc-searchbar__input-suffix d-flex justify-content-center" @click="searchOnText()">
                <font-awesome-icon class="align-self-center" :icon="['far', 'search']" />
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import axios from 'axios';
import debounce from 'lodash/debounce';
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { SuggestionCategory } from '../../../../functions/src/services/search/contracts/SuggestionCategory';
import { ISuggestion } from '../../../../functions/src/shared/contracts/api/SuggestionContract';
import { Constants } from '../../shared/Constants';
import { logService } from '../../shared/services/LogService';
import { eventHub } from '../EventHub.vue';
import { CategoryTag, GlossTag, ITag, LabelTag, TextTag } from './TagModel';

const search = namespace('search');

@Component({
    components: {
        VueTypeaheadBootstrap,
    },
})
export default class SearchBar extends Vue {
    @Prop({ default: true }) public showPrefix!: boolean;
    public searchText: string | null = null;
    public searchTarget: Vue | null = null;
    public suggestions: ISuggestion[] = [];

    public containerWidth: number = 0;

    private debouncedSuggest = debounce(() => this.suggest(), 200);
    private resizeHandler!: () => void;
    private resizeObserver?: ResizeObserver;

    @search.Action('updateTags')
    private updateTags!: ({
        tagsToAdd,
        tagsToRemove,
        replace,
    }: {
        tagsToAdd: ITag[];
        tagsToRemove: ITag[];
        replace: Boolean;
    }) => Promise<void>;

    @search.Action('initiateTextSearch')
    private initiateTextSearch!: () => Promise<void>;

    @search.Action('stopTextSearch')
    private stopTextSearch!: () => Promise<void>;

    public created(): void {
        this.resizeHandler = () => {
            const element = (this.$refs.container as HTMLElement) || (this.$el as HTMLElement);
            if (element) {
                this.containerWidth = element.clientWidth;
            }
        };
    }

    public get inputClass(): string {
        return this.showPrefix
            ? 'vgtc-searchbar__input'
            : 'vgtc-searchbar__input vgtc-searchbar__input--no-prefix';
    }

    public mounted(): void {
        // TODO: Keep an eye on https://github.com/alexurquhart/vue-bootstrap-typeahead/issues/7 It's about
        // adding more input event handling to the typeahead component
        const typeahead = this.$refs.typeahead as Vue;
        const input = typeahead.$el.querySelector('input') as HTMLInputElement;
        input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                this.searchOnText();
            }
        });
        input.addEventListener('focus', (event) => {
            eventHub.emitAppEvent('searchBarFocussed');
            this.initiateTextSearch();
        });
        input.addEventListener('blur', (event) => {
            this.stopTextSearch();
        });
        // These classes are a pain, remove them
        (typeahead.$el.querySelector('div') as HTMLDivElement).classList.remove('input-group');
        input.classList.remove('form-control');

        eventHub.onAppEvent('newSearchTriggered', () => {
            input.focus();
        });

        // Observe container width to switch placeholder based on actual space
        const container = (this.$refs.container as HTMLElement) || (this.$el.querySelector('.vgtc-searchbar') as HTMLElement);
        if (container && typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.resizeHandler());
            this.resizeObserver.observe(container);
        }
        window.addEventListener('resize', this.resizeHandler);
        this.resizeHandler();
    }

    public destroyed(): void {
        window.removeEventListener('resize', this.resizeHandler);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    public searchOnTag(suggestion: ISuggestion): Promise<void> {
        const tag = this.createTag(suggestion);
        return this.search(tag);
    }

    public async searchOnText(): Promise<void> {
        const cleanedText = this.normalizeSearchText(this.searchText || '');
        if (!cleanedText) {
            return;
        }
        const tag = TextTag(cleanedText);
        return await this.search(tag);
    }

    private normalizeSearchText(value: string): string {
        const trimmed = value.trim();
        if (!trimmed) {
            return '';
        }
        return trimmed.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    }

    public get searchPlaceholder(): string {
        const width = this.containerWidth || 0;
        return width < 380
            ? 'search.placeholder_veryshort'
            : width < 576
            ? 'search.placeholder_short'
            : 'search.placeholder';
    }

    public getIcon(category: SuggestionCategory): string[] | string {
        switch (category) {
            case SuggestionCategory.Translation:
                return ['fas', 'sign-language'];
            case SuggestionCategory.Category:
                return ['fas', 'th-large'];
            case SuggestionCategory.Label:
                return ['fas', 'tag'];
        }
    }

    private async search(tag: ITag): Promise<void> {
        this.searchText = '';
        // TODO: Currently the typeahead component does not repsond to clearing searchText
        // An open pull request fixes this (https://github.com/alexurquhart/vue-bootstrap-typeahead/pull/38)
        // so update the component after the pull request has been accepted
        (this.$refs.typeahead as any).inputValue = '';
        try {
            await this.updateTags({ tagsToAdd: [tag], tagsToRemove: [], replace: true });
            if (this.$route.name !== 'search') {
                this.$router.push('search');
            }
        } catch (err: any) {
            logService.logError(err);
        }
    }

    private createTag(suggestion: ISuggestion): ITag {
        switch (suggestion.category) {
            case SuggestionCategory.Translation:
                return TextTag(suggestion.text);
            case SuggestionCategory.Label:
                return LabelTag(suggestion.text, suggestion.id);
            case SuggestionCategory.Category:
                return CategoryTag(suggestion.text, suggestion.id);
            default:
                return TextTag(suggestion.text);
        }
    }

    @Watch('searchText')
    private onSearchChange() {
        this.debouncedSuggest();
    }

    private async suggest(): Promise<void> {
        const cleanedText = this.normalizeSearchText(this.searchText || '');
        if (!cleanedText) {
            this.suggestions = [];
            return;
        }
        const response = await axios.get<ISuggestion[]>(`${Constants.baseApiUrl}/suggest/all`, {
            params: {
                q: cleanedText,
            },
        });
        let suggestions = response.data;
        suggestions.forEach((suggestion) => {
            const duplicates = suggestions.filter(
                (duplicate) =>
                    duplicate.category === suggestion.category &&
                    duplicate.text === suggestion.text &&
                    duplicate.id !== suggestion.id,
            );
            if (duplicates.length) {
                suggestion.text = `${suggestion.text} (${suggestion.id})`;
                duplicates.forEach((duplicate) => {
                    duplicate.text = `${duplicate.text} (${duplicate.id})`;
                });
            }
        });

        const query = (this.searchText || '').toLowerCase();

        const getComparableText = (text: string): string => {
            return (text || '').replace(/\s*\(\d+\)\s*$/, '').toLowerCase();
        };

        const rank = (text: string): { exact: boolean; prefix: boolean; index: number } => {
            const t = getComparableText(text);
            const idx = query ? t.indexOf(query) : -1;
            return {
                exact: query ? t === query : false,
                prefix: query ? t.startsWith(query) : false,
                index: idx < 0 ? Number.MAX_SAFE_INTEGER : idx,
            };
        };

        suggestions = suggestions.sort((a, b) => {
            const ra = rank(a.text);
            const rb = rank(b.text);
            if (ra.exact !== rb.exact) return ra.exact ? -1 : 1;

            if (ra.prefix !== rb.prefix) return ra.prefix ? -1 : 1;

            if (ra.index !== rb.index) return ra.index - rb.index;

            return getComparableText(a.text).localeCompare(getComparableText(b.text));
        });

        this.suggestions = suggestions.map((s, i) => {
            (s as any).nr = `e_${i}`;
            return s;
        });
    }
}
</script>

<style lang="scss">
$input-height: 2.5rem;
.vgtc-searchbar {
    position: relative;

    .list-group-item.active {
        background-color: $background-tag-color;
        border-color: $background-tag-color;
        color: $background-tag-text-color;
    }
}

.vgtc-searchbar__input-prefix {
    color: $background-divider-color;
    position: absolute;
    width: 1 + $input-height;
    font-family: $font-family-serif;
    font-size: 1.5rem;
    height: $input-height;
}

.vgtc-searchbar__input {
    border-radius: 0.4rem;
    box-shadow: $box-shadow-searchbar;
    background-color: $background-color;
    color: $background-text-color;
    border: $border-searchbar;
    @include media-breakpoint-up(md) {
        border-radius: 0.2rem;
        box-shadow: $box-shadow-searchbar-long;
    }
    height: $input-height;
    padding: 0;
    padding-left: 1rem;

    &::placeholder {
        color: $background-divider-color;
        font-style: italic;
    }

    @include media-breakpoint-up(sm) {
        padding-left: 1 + $input-height;
    }

    width: 100%;

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
        display: none;
    }
}

.vgtc-searchbar__input--no-prefix {
    @include media-breakpoint-up(sm) {
        padding-left: 1rem; // no reserved space when prefix is hidden
    }
}

.vgtc-searchbar__input-suffix {
    color: $accent-color;
    position: absolute;
    width: 1 + $input-height;
    font-family: $font-family-serif;
    font-size: 1.25rem;
    right: 0;
    height: $input-height;
    cursor: pointer;
}

.vgtc-searchbar__input__suggestion__icon {
    width: 3rem;
    text-align: center;

    &--no-category {
        font-family: $font-family-serif;
    }
}

.vgtc-searchbar .vbt-autcomplete-list {
    text-align: left;
}
.vgtc-searchbar .list-group-item {
    text-align: left;
}
.vgtc-searchbar__input__suggestion {
    justify-content: flex-start;
}
.vgtc-searchbar__input__suggestion__text {
    text-align: left;
}
</style>
